import dayjs from 'dayjs';
import type { FieldConfig, ForgeQuery } from './types';
import { loadRememberedValues } from './remember';

export function forgeQueryToFormValues(fields: FieldConfig[], defaultQuery?: ForgeQuery): Record<string, any> {
  if (!defaultQuery?.filter) {
    return {};
  }

  const flatten = (obj: any, prefix: string, out: Record<string, any>) => {
    if (!obj || typeof obj !== 'object') return;
    const isLeaf = 'operator' in obj && 'value' in obj && Object.keys(obj).length <= 3;
    if (isLeaf) {
      out[prefix] = obj;
      return;
    }
    Object.keys(obj).forEach((k) => {
      const nextPrefix = prefix ? `${prefix}.${k}` : k;
      flatten(obj[k], nextPrefix, out);
    });
  };

  const flat: Record<string, any> = {};
  flatten(defaultQuery.filter as any, '', flat);

  const initialValues: Record<string, any> = {};
  const isEqualValue = (a: any, b: any) => {
    if (Array.isArray(a) || Array.isArray(b)) {
      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      if (a.length !== b.length) return false;
      return a.every((v, idx) => v == b[idx]);
    }
    return a == b;
  };
  fields.forEach((field) => {
    const qualifiedKey = field.group ? `${field.group}.${field.key}` : field.key;
    const filterValue = flat[qualifiedKey];
    if (field.type === 'select') {
      const options = field.getOptions ? field.getOptions({}) : field.options;
      const matched = options?.find((opt) => {
        if (!opt.queryValue) return false;
        const queryKey = field.group ? `${field.group}.${opt.queryValue.key || field.key}` : (opt.queryValue.key || field.key);
        const qv = flat[queryKey];
        if (!qv) return false;
        const operator = opt.queryValue.operator || 'eq';
        if (qv.operator !== operator) return false;
        return isEqualValue(qv.value, opt.queryValue.value);
      });
      if (matched) {
        initialValues[field.key] = matched.value;
        return;
      }
    }
    if (field.type === 'cascader') {
      const cascaderOptions = field.getCascaderOptions ? field.getCascaderOptions({}) : field.cascaderOptions || [];
      const matchedPaths: any[][] = [];
      const fallbackFilterValue = flat[qualifiedKey];
      const pushMatchedPath = (path: any[]) => {
        const key = path.map((segment) => String(segment)).join('||');
        if (matchedPaths.some((item) => item.map((segment) => String(segment)).join('||') === key)) {
          return;
        }
        matchedPaths.push(path);
      };
      const walkOptions = (options: any[], parentPath: any[] = []) => {
        options.forEach((opt) => {
          const currentPath = [...parentPath, opt.value];
          if (opt.queryValue) {
            const queryKey = field.group
              ? `${field.group}.${opt.queryValue.key || field.key}`
              : (opt.queryValue.key || field.key);
            const qv = flat[queryKey];
            const operator = opt.queryValue.operator || field.operators?.[0] || 'eq';
            if (qv && qv.operator === operator && isEqualValue(qv.value, opt.queryValue.value)) {
              pushMatchedPath(currentPath);
            } else if (qv && qv.operator === 'in' && Array.isArray(qv.value) && qv.value.some((v: any) => v == opt.queryValue.value)) {
              pushMatchedPath(currentPath);
            } else if (qv && (qv.operator === 'eq' || qv.operator === 'equal') && qv.value == opt.queryValue.value) {
              pushMatchedPath(currentPath);
            }
          } else if ((!opt.children || opt.children.length === 0) && fallbackFilterValue) {
            const operator = fallbackFilterValue.operator;
            if ((operator === 'eq' || operator === 'equal') && fallbackFilterValue.value == opt.value) {
              pushMatchedPath(currentPath);
            }
            if (operator === 'in' && Array.isArray(fallbackFilterValue.value) && fallbackFilterValue.value.some((v: any) => v == opt.value)) {
              pushMatchedPath(currentPath);
            }
          }
          if (Array.isArray(opt.children) && opt.children.length > 0) {
            walkOptions(opt.children, currentPath);
          }
        });
      };
      walkOptions(cascaderOptions as any[]);
      if (matchedPaths.length > 0) {
        initialValues[field.key] = field.cascaderProps?.multiple ? matchedPaths : matchedPaths[0];
        return;
      }
    }
    if (!filterValue) return;

    if (filterValue.operator === 'between' && Array.isArray(filterValue.value)) {
      if (field.type === 'date') {
        initialValues[field.key] = filterValue.value.map((v: string) => dayjs(v));
      } else {
        initialValues[field.key] = filterValue.value;
      }
    } else {
      if (field.type === 'multiSelect') {
        initialValues[field.key] = Array.isArray(filterValue.value) ? filterValue.value : [filterValue.value];
      } else {
        initialValues[field.key] = filterValue.value;
      }
    }
  });

  return initialValues;
}

export function buildForgeQueryFromValues(fields: FieldConfig[], values: Record<string, any>): ForgeQuery {
  const query: ForgeQuery = { filter: {} as any };

  const findByPath = (options: any[], path: any[]) => {
    let current: any = { children: options };
    for (const seg of path) {
      const next = (current.children || []).find((c: any) => c.value == seg);
      if (!next) return undefined;
      current = next;
    }
    return current;
  };

  Object.entries(values).forEach(([key, value]) => {
    const field = fields.find((f) => f.key === key);
    if (!field) return;
    if (field.noQuery) return;

    if (value === undefined || value === null) return;
    if (typeof value === 'string' && value.trim() === '') return;

    if (Array.isArray(value)) {
      if (field.type === 'cascader') {
        if (value.length === 0) return;
        const maybePath = value[0];
        const isMultiple = Array.isArray(maybePath);
        if (isMultiple) {
          const valid = value.filter((p) => Array.isArray(p) && p.length > 0);
          if (valid.length === 0) return;
        } else {
          const lastVal = value[value.length - 1];
          if (lastVal === undefined || lastVal === null) return;
          if (lastVal === '') return;
        }
      }
      if (field.type === 'date') {
        if (value.length !== 2 || !value[0] || !value[1]) return;
      } else {
        if (value.length === 0) return;
      }
    }

    let operator = 'eq';
    let processedValue = value;
    let leafKey = key;

    const currentOptions = field.getOptions ? field.getOptions(values) : field.options;
    if (field.type === 'select' && currentOptions) {
      const selectedOption = currentOptions.find((opt) => opt.value == value);
      if (selectedOption && selectedOption.queryValue) {
        if (selectedOption.queryValue.key) {
          leafKey = selectedOption.queryValue.key;
        }
        operator = selectedOption.queryValue.operator || 'eq';
        processedValue = selectedOption.queryValue.value;
      } else if (selectedOption) {
        processedValue = selectedOption.value;
      }
    } else if (field.type === 'multiSelect' && currentOptions && Array.isArray(value)) {
      const collected: any[] = [];
      value.forEach((v) => {
        const opt = currentOptions.find((o) => o.value == v);
        if (opt?.queryValue) {
          const qv = opt.queryValue.value;
          if (Array.isArray(qv)) {
            qv.forEach((item) => collected.push(item));
          } else {
            collected.push(qv);
          }
        } else if (opt) {
          collected.push(opt.value);
        } else {
          collected.push(v);
        }
      });
      const dedup = Array.from(new Set(collected));
      operator = 'in';
      processedValue = dedup;
    } else if (field.type === 'cascader') {
      const cascaderOptions = field.getCascaderOptions ? field.getCascaderOptions(values) : (field as any).cascaderOptions || [];
      const isMultiple = Array.isArray(value) && Array.isArray(value[0]);

      if (isMultiple) {
        const paths: any[][] = (value as any[][]).filter((p) => Array.isArray(p) && p.length > 0);
        const collected: any[] = [];
        const leafKeys = new Set<string>();

        paths.forEach((pathValues) => {
          const lastVal = pathValues[pathValues.length - 1];
          if (lastVal === undefined || lastVal === null || lastVal === '' || lastVal === 'all') return;

          const selectedNode = findByPath(cascaderOptions as any[], pathValues);
          if (selectedNode?.queryValue?.key) leafKeys.add(selectedNode.queryValue.key);
          if (!selectedNode?.queryValue && Array.isArray(selectedNode?.children) && selectedNode.children.length > 0) return;

          const rawVal = selectedNode?.queryValue ? selectedNode.queryValue.value : lastVal;
          if (rawVal === undefined || rawVal === null || rawVal === '' || rawVal === 'all') return;

          if (Array.isArray(rawVal)) rawVal.forEach((v) => collected.push(v));
          else collected.push(rawVal);
        });

        const dedup = Array.from(new Set(collected)).filter((v) => v !== undefined && v !== null && v !== '');
        if (dedup.length === 0) return;

        if (leafKeys.size === 1) leafKey = Array.from(leafKeys)[0];
        if (leafKeys.size > 1) return;

        operator = 'in';
        processedValue = dedup;
      } else {
        const pathValues: any[] = Array.isArray(value) ? value : [];
        const lastVal = pathValues[pathValues.length - 1];
        const selectedNode = findByPath(cascaderOptions as any[], pathValues);
        if (!selectedNode?.queryValue && (lastVal === '' || lastVal === 'all')) return;
        if (selectedNode?.queryValue) {
          if ((selectedNode.queryValue as any).key) {
            leafKey = (selectedNode.queryValue as any).key;
          }
          operator = selectedNode.queryValue.operator || (field.operators?.[0] || 'eq');
          processedValue = selectedNode.queryValue.value;
        } else {
          operator = field.operators?.[0] || 'eq';
          processedValue = lastVal;
        }
      }
      if (processedValue === undefined || processedValue === null || processedValue === '') return;
    } else if (field.operators && field.operators.length > 0) {
      operator = field.operators[0];
    } else {
      if (field.type === 'date' && Array.isArray(value) && value.length === 2) {
        operator = 'between';
      } else if (field.type === 'number' && Array.isArray(value) && value.length === 2) {
        operator = 'between';
      } else if (Array.isArray(value) && value.length > 0) {
        operator = 'in';
      } else if (field.type === 'string' && typeof value === 'string') {
        operator = 'like';
      }
    }

    if (field.type === 'date' && Array.isArray(value) && value.length === 2) {
      processedValue = value.map((v: dayjs.Dayjs) => v.format('YYYY-MM-DD'));
    }

    const groupSegments = field.group ? field.group.split('.') : [];
    // 仅当有 group 时拆分 key 构建嵌套对象；无 group 的 . key 保持扁平（用于 ForgeJoinDefs 关联查询）
    const keySegments = field.group && typeof leafKey === 'string' && leafKey.includes('.') ? leafKey.split('.') : [leafKey];
    const segments = [...groupSegments, ...keySegments];
    const leaf = { operator, value: processedValue };
    let cursor: any = query.filter as any;
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      const isLeaf = i === segments.length - 1;
      if (isLeaf) {
        cursor[seg] = leaf;
      } else {
        if (!cursor[seg] || typeof cursor[seg] !== 'object' || ('operator' in cursor[seg])) {
          cursor[seg] = {};
        }
        cursor = cursor[seg];
      }
    }
  });

  return query;
}

export function mergeDefaultQueryWithRemembered(fields: FieldConfig[], defaultQuery?: ForgeQuery): { query: ForgeQuery; values: Record<string, any> } {
  const fromDefault = forgeQueryToFormValues(fields, defaultQuery);
  const remembered = loadRememberedValues(fields);
  const values = { ...fromDefault, ...remembered };
  const query = buildForgeQueryFromValues(fields, values);
  return { query, values };
}
